import { Link, useLocation, useNavigate } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { usePOCounts } from '@/hooks/usePOs'
import { OPEN_STATUSES, useRequestCounts } from '@/hooks/useProductRequests'
import { useAuthStore } from '@/store/authStore'
import { SyncOffBanner } from '@/components/shared/SyncOffBanner'

const TABS = [
  { id: 'dashboard', label: 'Dashboard', path: '/inventory' },
  { id: 'po', label: 'Purchase orders', path: '/purchase-orders' },
  { id: 'requests', label: 'Requests', path: '/product-requests' },
  { id: 'events', label: 'Events', path: '/events' },
  { id: 'stocktakes', label: 'Stocktakes', path: '/stocktakes' },
  { id: 'adjustments', label: 'Adjustments', path: '/adjustments' },
  { id: 'vendors', label: 'Vendors', path: '/vendors' },
  { id: 'catalog', label: 'KH Catalog', path: '/catalog' },
  { id: 'marketing', label: 'Marketing', path: '/marketing' },
  { id: 'admin', label: 'Admin', path: '/admin' },
]

export function TopTabs() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const counts = usePOCounts()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const poOpen = counts.DRAFT + counts.AWAITING_APPROVAL + counts.APPROVED
  // One cheap /counts call (staleTime 60s) shared with the Product Requests
  // page via the query cache — the badge shows work still in the pipeline, so
  // PURCHASED / CLOSED_NO_PURCHASE are excluded.
  const { data: requestCounts } = useRequestCounts()
  const requestsOpen = requestCounts
    ? OPEN_STATUSES.reduce((sum, s) => sum + (requestCounts[s] ?? 0), 0)
    : 0

  function countFor(id: string): number | undefined {
    if (id === 'po') return poOpen || undefined
    if (id === 'requests') return requestsOpen || undefined
    return undefined
  }

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <>
      <nav className="dash-tabbar" aria-label="Main navigation">
        {TABS.map((tab) => {
          const active = pathname === tab.path || pathname.startsWith(`${tab.path}/`)
          const count = countFor(tab.id)
          return (
            <Link
              key={tab.id}
              to={tab.path}
              className={`dash-tab${active ? ' active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              {tab.label}
              {count != null && <span className="tab-count">{count}</span>}
            </Link>
          )
        })}
        <div className="flex-1" />
        {user && (
          <span className="dash-tab" style={{ pointerEvents: 'none', cursor: 'default' }}>
            {user.display_name || user.username}
          </span>
        )}
        <button
          type="button"
          onClick={handleLogout}
          className="dash-tab"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut size={13} aria-hidden="true" />
          <span>Sign out</span>
        </button>
      </nav>
      {/* Directly under the nav so every page with TopTabs shows it. */}
      <SyncOffBanner />
    </>
  )
}
